import { Button } from '@/shared/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/Form';
import { FormSelect } from '@/shared/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { buildMeetingFunnelOptionGroups } from '../model/funnels';
import type { MeetingSlotFormState } from '../model/useMeetingSlotForm';
import { RefreshCcw } from 'lucide-react';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

type MeetingSlotFormProps = {
  form: MeetingSlotFormState;
  selectedSlot: string;
  onTimeClick: () => void;
};

function getUniqueOptions(options: string[]) {
  return Array.from(
    new Set(options.map((option) => option.trim()).filter(Boolean))
  );
}

function withSelectedOption(options: string[], selected: string) {
  const trimmed = selected.trim();
  if (!trimmed) {
    return options;
  }
  if (options.includes(trimmed)) {
    return options;
  }
  return [trimmed, ...options];
}

export function MeetingSlotForm({
  form,
  onTimeClick,
  selectedSlot,
}: MeetingSlotFormProps) {
  const leadLabel = useWatch({
    control: form.control,
    name: 'leadLabel',
  });
  const projectTitle = useWatch({
    control: form.control,
    name: 'projectTitle',
  });
  const dealLabel = useWatch({
    control: form.control,
    name: 'dealLabel',
  });

  const funnelOptionGroups = useMemo(
    () => buildMeetingFunnelOptionGroups(form.funnelOptions),
    [form.funnelOptions]
  );
  const leadOptions = useMemo(
    () =>
      withSelectedOption(
        getUniqueOptions(form.leadOptions),
        String(leadLabel ?? '')
      ),
    [form.leadOptions, leadLabel]
  );
  const projectOptions = useMemo(
    () =>
      withSelectedOption(
        getUniqueOptions(form.projectOptions),
        String(projectTitle ?? '')
      ),
    [form.projectOptions, projectTitle]
  );
  const dealOptions = useMemo(
    () =>
      withSelectedOption(
        getUniqueOptions(form.dealOptions),
        String(dealLabel ?? '')
      ),
    [form.dealOptions, dealLabel]
  );
  const presenterOptions = getUniqueOptions(form.presenterOptions);

  return (
    <Form {...form.formMethods}>
      <form className="mt-6 px-3 pt-2" onSubmit={form.handleSave}>
        <div className="grid grid-cols-3 gap-4">
          <FormItem>
            <span className="text-sm font-medium">Время</span>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full justify-between"
              onClick={onTimeClick}
            >
              {selectedSlot}
              <RefreshCcw />
            </Button>
          </FormItem>

          <FormField
            control={form.control}
            name="meetingFormat"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Формат</FormLabel>
                <FormControl>
                  <Tabs value={field.value} onValueChange={field.onChange}>
                    <TabsList size="lg">
                      <TabsTrigger value="Онлайн">Онлайн</TabsTrigger>
                      <TabsTrigger value="Оффлайн">Оффлайн</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="meetingType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Тип</FormLabel>
                <FormControl>
                  <Tabs value={field.value} onValueChange={field.onChange}>
                    <TabsList size="lg">
                      <TabsTrigger value="Продажа">Продажа</TabsTrigger>
                      <TabsTrigger value="Демо">Демо</TabsTrigger>
                      <TabsTrigger value="Аудит">Аудит</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormSelect
            name="funnelLabel"
            label="Воронка"
            optionGroups={funnelOptionGroups}
            loading={form.isFunnelsLoading}
            size="lg"
          />

          {form.funnelKind === 'lead' ? (
            <FormSelect
              name="leadLabel"
              label="Лид"
              options={leadOptions}
              placeholder="Найти лид..."
              loading={form.isLeadsLoading}
              onOpenChange={(open) => form.handleLeadOpenChange(open)}
              onValueChange={form.handleLeadChange}
              size="lg"
            />
          ) : null}

          {form.funnelKind === 'smart_process' ? (
            <FormSelect
              name="projectTitle"
              label="Проект"
              options={projectOptions}
              placeholder="Найти проект..."
              loading={form.isProjectsLoading}
              onOpenChange={(open) => form.handleProjectOpenChange(open)}
              onValueChange={form.handleProjectChange}
              size="lg"
            />
          ) : null}

          {form.funnelKind === 'deal' ? (
            <FormSelect
              name="dealLabel"
              label="Сделка"
              options={dealOptions}
              placeholder="Найти сделку..."
              loading={form.isDealsLoading}
              onOpenChange={(open) => form.handleDealOpenChange(open)}
              onValueChange={form.handleDealChange}
              size="lg"
            />
          ) : null}

          <FormSelect
            name="presenterName"
            label="Кто проведет?"
            options={presenterOptions}
            placeholder="Найти сотрудника..."
            loading={form.isUsersLoading}
            onValueChange={form.handlePresenterChange}
            onOpenChange={(open, value) => {
              if (open) {
                form.handlePresenterSearchQueryChange(value);
              }
            }}
            size="lg"
          />
        </div>

        <div className="mt-6 flex justify-center">
          <Button
            type="submit"
            size={'lg'}
            disabled={form.isSaving || !selectedSlot}
          >
            {form.isSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
